import { Link } from 'react-router-dom';

import { cn } from '@/utils/cn';

/**
 * The frame every list page sits in: one card holding the toolbar, the
 * table and the pager.
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ [All 22] [Pending 3] [Verified]          🔍 Search  [▾]  │  toolbar
 *   ├──────────────────────────────────────────────────────────┤
 *   │ NAME            ROLE        STATUS        JOINED    ⋯    │  tinted head
 *   │ …rows, edge to edge…                                     │
 *   ├──────────────────────────────────────────────────────────┤
 *   │ Showing 1–20 of 335                   ‹ Page 1 of 17 ›   │  footer
 *   └──────────────────────────────────────────────────────────┘
 *
 * `toolbarStart` is for the status tabs (or a count), `toolbarEnd` for the
 * search box and compact filters. The table goes in as children and runs to
 * the card's edges -- the cell padding and the tinted header row come from
 * `.list-card` in index.css, so a page writes plain `<th>`/`<td>` and still
 * matches every other list. `state` replaces the table with a padded loading,
 * empty or error message; pass `null` when there are rows to show.
 */
export function ListCard({
  toolbarStart,
  toolbarEnd,
  state,
  footer,
  isRefreshing = false,
  fill = false,
  className,
  children,
}) {
  const hasToolbar = Boolean(toolbarStart || toolbarEnd);
  return (
    <section
      className={cn(
        'list-card bg-surface border-line rounded-card shadow-card min-w-0 overflow-hidden border',
        // `fill`: when the page gives the card extra height, the body takes
        // it and an empty/upgrade state stretches with it, centred, rather
        // than leaving a blank band under the card.
        fill && 'flex flex-col',
        className,
      )}
    >
      {hasToolbar && (
        <div className="border-line flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2 sm:px-4">
          <div className="tab-strip max-w-full min-w-0">{toolbarStart}</div>
          {toolbarEnd && (
            <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
              {toolbarEnd}
            </div>
          )}
        </div>
      )}

      {state ? (
        <div
          className={cn(
            'p-4',
            fill &&
              'flex flex-1 flex-col *:flex *:flex-1 *:flex-col *:justify-center [&>*>*:only-child]:flex-1 [&>*>*:only-child]:justify-center',
          )}
        >
          {state}
        </div>
      ) : (
        <div
          className={cn(
            'overflow-x-auto transition-opacity',
            fill && 'flex-1',
            isRefreshing && 'pointer-events-none opacity-60',
          )}
          aria-busy={isRefreshing || undefined}
        >
          {children}
        </div>
      )}

      {footer && !state && <div className="border-line border-t px-3 py-2 sm:px-4">{footer}</div>}
    </section>
  );
}

/** Two letters is enough identity for a 36px circle. */
function initials(name) {
  if (!name) return '?';
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');
}

/**
 * The first cell of a row that names a person or a company: a round avatar,
 * the name, and one quieter line beneath it (an email, a location).
 *
 * `to` makes the name the row's link, which is where an admin expects to
 * click. `imageUrl` shows a logo or photo when there is one; otherwise the
 * initials stand in, in the primary colour.
 */
export function EntityCell({ name, subtitle, to, imageUrl, className }) {
  const label = to ? (
    <Link to={to} className="text-ink hover:text-brand-700 font-medium transition">
      {name}
    </Link>
  ) : (
    <span className="text-ink font-medium">{name}</span>
  );

  return (
    <div className={cn('flex min-w-0 items-center gap-3', className)}>
      {imageUrl ? (
        <img
          src={imageUrl}
          alt=""
          className="border-line size-8 shrink-0 rounded-full border object-cover"
        />
      ) : (
        <span
          aria-hidden="true"
          className="bg-brand-600 grid size-8 shrink-0 place-items-center rounded-full text-[11px] font-semibold text-white"
        >
          {initials(name)}
        </span>
      )}
      <div className="min-w-0">
        <div className="truncate">{label}</div>
        {subtitle && <div className="text-ink-muted truncate text-xs">{subtitle}</div>}
      </div>
    </div>
  );
}
